import {
  AdminSpinner,
  Box,
  Button,
  Div,
  EditPenIcon,
  H3,
  H5,
  Icon,
  P,
  PlusIcon,
  Separator,
  SuccessToast,
  TrashIconRed,
  getBrandsHolidayHours,
  getLocationHolidayHours,
  updateBrandsHolidayHours,
  updateLocationsHolidayHours,
  reportError,
} from '@vgrubs/components';
import { SimpleBrandEntity } from 'components/src/types/brand.types';
import {
  HolidayHoursReponseV2,
  SimpleLocationEntity,
} from 'components/src/types/location.types';
import { DateTime } from 'luxon';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { convert24HourTo12Hour } from '../../../helpers/time-helpers';
import { AddHolidayHours } from '../AddHolidayHours';
import { DeleteHolidayHours } from '../DeleteHolidayHours';
import { normalizeHolidayHours, stableStringifyHolidayHours } from '../helpers';
import { ApplyToAllBrandsModal } from '../ApplyToAllBrandsModal';

export const HolidayHours = ({
  addDayModal,
  setAddDayModal,
  selectedBrand,
  selectedLocation,
}: {
  addDayModal:
    | boolean
    | { edit: HolidayHoursReponseV2 | null; index?: number }
    | null;
  setAddDayModal: Dispatch<
    SetStateAction<
      boolean | { edit: HolidayHoursReponseV2 | null; index?: number } | null
    >
  >;
  selectedBrand: SimpleBrandEntity | null;
  selectedLocation: SimpleLocationEntity | null;
}) => {
  const [loading, setLoading] = useState(false);
  const [applyToAllBrands, setApplyToAllBrands] = useState(false);
  const [holidayHours, setHolidayHours] = useState<
    HolidayHoursReponseV2[] | null
  >(null);

  const initialSnapshotRef = useRef<string>('[]');

  const [deleteDayModal, setDeleteDayModal] = useState<{
    index: number;
  } | null>(null);

  async function getHolidayHours() {
    if (!selectedLocation) return;

    const locationOrBrandFunction = selectedBrand
      ? getBrandsHolidayHours
      : getLocationHolidayHours;

    const locationOrBrandId = selectedBrand
      ? selectedBrand.id
      : selectedLocation.id;

    try {
      setLoading(true);

      const { data } = await locationOrBrandFunction(locationOrBrandId);

      setHolidayHours(normalizeHolidayHours(data.data));
      initialSnapshotRef.current = stableStringifyHolidayHours(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(applyToAllBrands: boolean) {
    if (!selectedLocation || !holidayHours) return;

    const locationOrBrandFunction = selectedBrand
      ? updateBrandsHolidayHours
      : updateLocationsHolidayHours;

    const locationOrBrandId = selectedBrand
      ? selectedBrand.id
      : selectedLocation.id;

    const body = {
      holidayHours,
      applyToAllBrands: selectedBrand ? undefined : applyToAllBrands,
    };

    try {
      setLoading(true);
      const { data } = await locationOrBrandFunction(locationOrBrandId, body);
      SuccessToast(data.message);
      setHolidayHours(normalizeHolidayHours(data.data));
      initialSnapshotRef.current = stableStringifyHolidayHours(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getHolidayHours();
  }, [selectedLocation, selectedBrand]);

  const isDirty = useMemo(() => {
    return (
      stableStringifyHolidayHours(holidayHours) !== initialSnapshotRef.current
    );
    // Or with lodash: return !isEqual(holidayHours, currentHolidayHours)
  }, [holidayHours]);

  const handleCancel = () => {
    setHolidayHours(JSON.parse(initialSnapshotRef.current));
  };

  return (
    <>
      {loading && <AdminSpinner isRootPage />}
      <>
        {Array.isArray(holidayHours) && (
          <>
            <Div
              $flex
              $between
              $alignCenter
              $m="16px 0"
              $mSM="12px 0"
              $gap="16px"
            >
              <div>
                <H3 $mb="0">Holiday Hours</H3>
                {holidayHours.length < 1 && (
                  <P $m="8px 0 0 0">
                    This {selectedBrand ? 'brand' : 'location'} currently
                    doesn't have holiday hours.
                  </P>
                )}
              </div>

              {holidayHours?.length < 1 ? (
                <Button
                  $selected
                  $small
                  onClick={() => setAddDayModal({ edit: null, index: 0 })}
                >
                  Add
                </Button>
              ) : (
                <Icon
                  $pointer
                  onClick={() => setAddDayModal({ edit: null, index: 0 })}
                >
                  <PlusIcon />
                </Icon>
              )}
            </Div>
            {holidayHours.map((day, i) => {
              return (
                <Box $flex $between $alignCenter $mb="8" key={i}>
                  <div>
                    <P $mb="4">
                      {DateTime.fromISO(day.date).toFormat('MMM dd, yyyy')}
                    </P>
                    <H5 $mb="0">
                      {convert24HourTo12Hour(day.startTime)} -{' '}
                      {convert24HourTo12Hour(day.endTime)}
                    </H5>
                  </div>

                  <Div $flex $between $alignCenter $gap="16px" $gapSM="8px">
                    <Icon
                      $pointer
                      $shadow
                      onClick={() =>
                        setAddDayModal({
                          edit: day,
                          index: i,
                        })
                      }
                    >
                      <EditPenIcon />
                    </Icon>
                    <Icon
                      $pointer
                      onClick={() => setDeleteDayModal({ index: i + 1 })}
                    >
                      <TrashIconRed />
                    </Icon>
                  </Div>
                </Box>
              );
            })}
            <Separator $m="16px 0" />
          </>
        )}
      </>

      {isDirty && (
        <Div $flex $justifyEnd $m="20px 0" $mSM="16px 0" $gap="16px">
          <Button $selected $gray onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            $selected
            onClick={() =>
              selectedBrand ? handleSave(true) : setApplyToAllBrands(true)
            }
          >
            Save
          </Button>
        </Div>
      )}

      <AddHolidayHours
        modalIsOpen={addDayModal}
        closeModal={() => setAddDayModal(null)}
        setHolidayHours={
          setHolidayHours as Dispatch<SetStateAction<HolidayHoursReponseV2[]>>
        }
      />

      <DeleteHolidayHours
        modalIsOpen={deleteDayModal}
        closeModal={() => setDeleteDayModal(null)}
        setHolidayHours={
          setHolidayHours as Dispatch<SetStateAction<HolidayHoursReponseV2[]>>
        }
      />
      <ApplyToAllBrandsModal
        isOpen={applyToAllBrands}
        setIsOpen={setApplyToAllBrands}
        handleSubmit={(v) => {
          handleSave(v);
          setApplyToAllBrands(false);
        }}
        text="Do you want to apply this holiday hours setup to all brands connected to this location?"
      />
    </>
  );
};
